import { formatAddress, isResource } from '@medplum/core'
import type {
  Address,
  CodeableConcept,
  ContactPoint,
  Location,
  Organization,
  Reference,
} from '@medplum/fhirtypes'
import {
  LOCATION_PHYSICAL_TYPE,
  LOCATION_SYSTEM,
  SGV_LOCATION_IDENTIFIER,
} from '#src/codeSystems.ts'
import { ExtendedResource } from '#src/ExtendedResource.ts'
import { invariant } from '#src/invariant.ts'
import { addressIdentifier } from '#src/models/patients/addressNormalizer.ts'
import type { QueryTypes } from '#src/search.ts'
import { isOfType } from '#src/utils.ts'

export class ExtendedLocation extends ExtendedResource<Location> {
  static readonly RESOURCE_TYPE = 'Location'

  static isInstance(value: unknown): value is Location {
    return isResource(value) && isOfType(value, 'Location')
  }

  /**
   * Base FHIR search query for this resource — extended/modified at call sites.
   * Changing this affects every caller. Grep `baseSearchQuery` for impact before editing.
   */
  static baseSearchQuery(): QueryTypes {
    return {
      status: 'active',
      _sort: '-_lastUpdated',
      _count: '1000',
    }
  }

  // Builds a Location from an address with a stable, address-derived identifier
  // so that multiple providers sharing a building converge on one Location.
  // Defaults to an "active" building; name falls back to the formatted address.
  static build(params: {
    address: Address
    managingOrganization?: Reference<Organization>
    name?: string
    telecom?: ContactPoint[]
    physicalType?: CodeableConcept
  }): ExtendedLocation {
    const identifierValue = addressIdentifier(params.address)
    invariant(
      identifierValue,
      'Cannot build a Location from an address with no meaningful content'
    )
    return new ExtendedLocation({
      status: 'active',
      identifier: [{ system: LOCATION_SYSTEM, value: identifierValue }],
      name: params.name ?? formatAddress(params.address),
      address: params.address,
      managingOrganization: params.managingOrganization,
      telecom: params.telecom,
      physicalType: params.physicalType ?? {
        coding: [
          { system: LOCATION_PHYSICAL_TYPE, code: 'bu', display: 'Building' },
        ],
      },
    })
  }

  // Idempotent lookup/upsert key matching `build`'s address-derived identifier.
  static byAddressIdentifier(address: Address): QueryTypes {
    const identifierValue = addressIdentifier(address)
    invariant(
      identifierValue,
      'Cannot derive a Location identifier from an address with no meaningful content'
    )
    return {
      status: 'active',
      identifier: { system: LOCATION_SYSTEM, value: identifierValue },
    }
  }

  get name(): string {
    if (!this.resource.name) {
      console.error(this.reference.reference, 'has no name')
    }
    return this.resource.name ?? ''
  }

  get address(): Address | undefined {
    return this.resource.address
  }

  get managingOrganization(): Reference<Organization> | undefined {
    return this.resource.managingOrganization
  }

  set managingOrganization(org: Reference<Organization> | undefined) {
    this.resource.managingOrganization = org
  }

  get addressString(): string {
    if (!this.address) {
      return ''
    }
    return formatAddress(this.address)
  }

  get searchKeywords(): string[] {
    return [this.name, this.addressString]
  }

  static searchSGVLocation(): QueryTypes {
    return {
      status: 'active',
      identifier: { system: LOCATION_SYSTEM, value: SGV_LOCATION_IDENTIFIER },
    }
  }

  protected getExportFields() {
    return {
      id: this.id,
      name: this.name,
      status: this.resource.status,
      address: this.resource.address,
      telecom: this.resource.telecom,
      type: this.resource.type,
      physicalType: this.resource.physicalType,
      managingOrganization: this.resource.managingOrganization,
    }
  }
}
